/**
 * Agent CRM per-section mini-tours (EPIC #1321, part 4/9).
 *
 * One short (3-7 step) deep-dive per agent section, phrased around the field
 * agent's everyday job -- launched opt-in from Training (7/9) or the first-visit
 * nudge (8/9), never forced. Registered in the central registry (1/9) so the
 * Guided Tours launcher (#1328) lists them automatically; each runs STANDALONE
 * from any starting page (the engine navigates to the section first), is
 * individually skippable, and records completion to TourProgress.
 *
 * Conventions (mirror welcomeTours.ts, the 3/9 template):
 *  - Copy rules (D4-ONB-011 / D4-WLB): agency-neutral via the ``{agency}`` token
 *    (never a hardcoded agency/vendor name -- the D4-WLB-008 grep guard stays
 *    green); plain professional language for non-technical realtors; NO emojis.
 *  - Gating (D4-ONB-004): a step declares the same permFlag / module gates the
 *    sidebar's ``itemVisible`` uses, so a step silently disappears when its
 *    surface is off -- Farm/Referrals steps gate on ``module: "farm"``,
 *    Messaging on ``module: "messaging"``, the Document Vault on
 *    ``module: "documents"``, and every My Stats money step on
 *    ``permFlag: "view_commission"`` (so value-free back-office presets never
 *    see them). A step whose target is momentarily absent is silently advanced
 *    past by the engine (TARGET_NOT_FOUND), so each tour opens with a ``center``
 *    narrative step that always renders.
 *  - Targets are stable ``data-tour="..."`` anchors. Sidebar items resolve to
 *    the auto-generated ``nav-<route>`` anchor (NavGroupSection) and declare the
 *    ``navGroup`` to expand (and, below lg:, the mobile drawer to open) before
 *    highlighting; in-page anchors live on the section's own page/component.
 *
 * REQs: D4-ONB-008 (agent mini-tours), D4-ONB-011 (agency-neutral copy).
 */
import type { TourDefinition } from "./types";

/**
 * The back-office permission presets the agent CRM section tours are WITHHELD
 * from (issue #1528). These personas work a single back-office surface, not the
 * agent CRM, so the agent section deep-dives must not surface in their Training
 * launcher or first-visit nudge (mirrors CrmSidebar.itemVisible / #1521: their
 * welcome tour is the one canonical journey). A plain field agent's preset is
 * NOT in this list (so they keep all six tours) and an admin reports no preset
 * (so an admin previewing the CRM view is unaffected). Kept in sync with the
 * PermissionPreset union in api/client.ts.
 *
 * #2453: HR is likewise a single-surface back-office persona, so the agent CRM
 * section deep-dives are withheld from it. Kept in sync with the crmNav.ts copy
 * (enforced by the backOfficePresetsDrift.test.ts guard).
 *
 * #2467: the CANVASSER is likewise a restricted back-office persona -- its whole
 * agent sales-book is hidden, so the agent CRM section deep-dives (which walk
 * listings/deals/money surfaces a canvasser cannot open) are withheld from it.
 * Its own guided journey is the canvasser-welcome tour.
 */
export const BACK_OFFICE_PRESETS: string[] = [
  "paralegal",
  "finance",
  "finance_accounts",
  "manager",
  "accounts",
  "office_admin",
  "hr",
  // D5-RBP-005 (#4175): the combined HR/Paralegal desk.
  "hr_paralegal",
  "canvasser",
];

/**
 * Prospecting -- Contacts / Farm / Referrals / Buyers.
 * The agent's people: who they know, the streets they work, referrals in flight,
 * and the buyers they are matching to stock. Farm + Referrals steps gate on the
 * ``farm`` module so they vanish when it is off.
 */
export const PROSPECTING_TOUR: TourDefinition = {
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  id: "agent-prospecting",
  title: "Prospecting tour",
  steps: [
    {
      id: "welcome",
      route: "/crm/contacts",
      target: "center",
      section: "Welcome",
      title: "Your people",
      body: "Prospecting is where you build and work your database -- your contacts, the streets you farm, referrals and your buyers. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "contacts",
      route: "/crm/contacts",
      target: "page-contacts",
      section: "Contacts",
      title: "Your contact book",
      body: "Capture and search every person you deal with here. Select several rows to act on them in bulk -- bulk archive keeps your book tidy without losing anything.",
      placement: "bottom",
    },
    {
      // ACTION step (issue #1342): beacon on the New Contact button -- the key
      // call-to-action of this section. Clicking the beacon reveals the tooltip;
      // it never clicks the button itself, so nothing is captured.
      id: "new-contact",
      route: "/crm/contacts",
      target: "contacts-new",
      kind: "action",
      section: "New contact",
      title: "Add a new contact",
      body: "New Contact is how you add someone to your book. Give the beacon a click to see where it sits -- then we will open it together.",
      placement: "left",
    },
    {
      // MODAL step (issue #1342): the engine opens the New Contact modal for you
      // (openVia clicks the safe, open-only button), waits for the dialog to
      // mount, then highlights it. Nothing is submitted -- close it any time with
      // Esc and the tour carries on.
      id: "new-contact-modal",
      route: "/crm/contacts",
      target: "contacts-new-modal",
      kind: "action",
      openVia: "contacts-new",
      section: "New contact",
      title: "The new-contact form",
      body: "This is the form you fill in: their name, how to reach them, and how you know them. Capture the detail and Create Contact adds them to your book. We have only opened it to show you -- nothing is saved while you look.",
      // Center the tooltip so it stays in the viewport ABOVE the open modal --
      // an auto-placed tooltip lands off-screen beside a tall dialog (#1342).
      placement: "center",
    },
    {
      id: "farm",
      route: "/crm/contacts",
      target: "nav-/crm/farm",
      section: "Farm",
      navGroup: "people",
      module: "farm",
      title: "Your farm",
      body: "Farm is your territory -- the streets and complexes you work. Keep your records current here so you always know your area better than anyone.",
      placement: "right",
    },
    {
      id: "referrals",
      route: "/crm/contacts",
      target: "nav-/crm/referrals",
      section: "Referrals",
      navGroup: "people",
      module: "farm",
      title: "Referrals",
      body: "Refer a property or a buyer to a colleague, and pick up referrals sent your way. You are notified when a referral becomes due.",
      placement: "right",
    },
    {
      id: "buyers",
      route: "/crm/contacts",
      target: "nav-/crm/buyers",
      section: "Buyers",
      navGroup: "people",
      title: "Your buyers",
      body: "Track each buyer through their stages and match them to your listings, so the right stock reaches the right buyer first. That is Prospecting -- explore at your own pace.",
      placement: "right",
    },
  ],
};

/**
 * Listings -- browse/filter, listing detail, Add Mandate front door.
 */
export const LISTINGS_TOUR: TourDefinition = {
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  id: "agent-listings",
  title: "Listings tour",
  steps: [
    {
      id: "welcome",
      route: "/crm/listings",
      target: "center",
      section: "Welcome",
      title: "Your properties",
      body: "Listings is your stock book -- every property you have on the market. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "browse",
      route: "/crm/listings",
      target: "page-listings",
      section: "Browse",
      title: "Browse and filter",
      body: "Search and filter your listings to find a property fast. Open any one to see its full detail, photos and the buyers it suits.",
      placement: "bottom",
    },
    {
      // ACTION step (issue #1342): beacon on the Add Mandate front door.
      id: "add-mandate",
      route: "/crm/listings",
      target: "listings-add-mandate",
      kind: "action",
      section: "Add Mandate",
      title: "Add a mandate",
      body: "Won a new mandate? Add Mandate is the front door. Give the beacon a click -- then we will open the form together.",
      placement: "left",
    },
    {
      // The Add Mandate front door opens a full-page wizard (not a modal), so the
      // engine simply navigates to it and highlights the empty form. Landing on a
      // blank form captures nothing.
      id: "add-mandate-form",
      route: "/crm/listings/add-mandate",
      target: "add-mandate-form",
      kind: "action",
      section: "Add Mandate",
      title: "Capture the mandate",
      body: "Start by picking the property from the farm book, or look it up on LOOM -- the address then comes from the deeds record, so it cannot be mistyped. Then capture the price and the terms and submit it for sign-off. We have only opened the form to show you; nothing is saved until you fill it in and submit.",
      placement: "bottom",
    },
  ],
};

/**
 * Deals -- pipeline list, deal detail, Add Sale front door, FICA Matrix.
 */
export const DEALS_TOUR: TourDefinition = {
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  id: "agent-deals",
  title: "Deals tour",
  steps: [
    {
      id: "welcome",
      route: "/crm/deals",
      target: "center",
      section: "Welcome",
      title: "Your sales",
      body: "Deals is where you track every sale from offer through to registration. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "pipeline",
      route: "/crm/deals",
      target: "page-deals",
      section: "Pipeline",
      title: "Your pipeline",
      body: "Every deal in flight, with its stage and value. Open one to work the file -- parties, conveyancer, documents and milestones.",
      placement: "bottom",
    },
    {
      // ACTION step (issue #1342): beacon on the Add Sale front door.
      id: "add-sale",
      route: "/crm/deals",
      target: "deals-add-sale",
      kind: "action",
      section: "Add Sale",
      title: "Add a sale",
      body: "Got a signed offer? Add Sale opens the file. Give the beacon a click -- then we will open the form together.",
      placement: "left",
    },
    {
      // Add Sale opens a full-page wizard (not a modal); the engine navigates to
      // it and highlights the empty form. Nothing is submitted.
      id: "add-sale-form",
      route: "/crm/deals/add-sale",
      target: "add-sale-form",
      kind: "action",
      section: "Add Sale",
      title: "Capture the sale",
      body: "Here you capture the buyer, the price and the conveyancer, and start the run to registration. Submitting sends it for manager sign-off. We have only opened the form to show you; nothing is saved until you fill it in and submit.",
      placement: "bottom",
    },
    {
      id: "fica-matrix",
      route: "/crm/deals",
      target: "nav-/crm/fica-matrix",
      section: "FICA Matrix",
      navGroup: "property-deals",
      title: "What is missing before you get paid",
      body: "The FICA Matrix flags the compliance documents still outstanding on each deal -- the paperwork that has to be done before a transfer can register. Clear the flags to keep your deals moving.",
      placement: "right",
    },
    {
      // ACTION step (issue #1342): land on the FICA Matrix page and beacon it as
      // the place you act to clear outstanding documents. The page navigation is
      // read-only -- it lists what is missing, it changes nothing.
      id: "fica-matrix-action",
      route: "/crm/fica-matrix",
      target: "page-fica-matrix",
      kind: "action",
      section: "FICA Matrix",
      title: "Clear what is outstanding",
      body: "Each flagged deal opens to show the exact documents still needed, party by party. Open the deal's FICA tab to start a party's FICA yourself -- Start FICA on each buyer or seller -- then verify and collect what is missing there. That is what keeps a transfer moving.",
      placement: "bottom",
    },
    {
      // The per-deal commission questionnaire (maker entry) lives on a per-deal
      // route (/crm/deals/:id/commission) with no fixed URL, so -- exactly like
      // the File Cover Page pattern (#1327) -- it is introduced from the deals
      // page rather than navigating to an unresolvable :id placeholder. A page
      // beacon on the pipeline, gated to view_commission so a value-free preset
      // never sees a money step.
      id: "commission-questionnaire",
      route: "/crm/deals",
      target: "page-deals",
      kind: "action",
      section: "Commission",
      permFlag: "view_commission",
      title: "Record the commission split",
      body: "Open any deal and you can fill in its commission questionnaire -- who shares the commission and in what proportion. You capture the split; it is then checked and signed off by someone else before anything is paid. That is your deals -- explore at your own pace.",
      placement: "bottom",
    },
  ],
};

/**
 * My Stats -- My Commission, My Targets, Monthly Activity, Leaderboard.
 * The money steps gate on ``view_commission`` (+ the ``financial`` module) so a
 * value-free back-office preset never sees them; Monthly Activity is an activity
 * barometer, not a money figure, so it stays ungated and keeps the tour alive
 * for a hypothetical no-commission custom preset.
 */
export const MY_STATS_TOUR: TourDefinition = {
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  id: "agent-my-stats",
  title: "My Stats tour",
  steps: [
    {
      id: "welcome",
      route: "/crm/kpis",
      target: "center",
      section: "Welcome",
      title: "Your numbers",
      body: "My Stats keeps your own performance a click away -- your earnings, your targets, your activity and where you stand. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "commission",
      route: "/crm/kpis",
      target: "nav-/crm/commission",
      section: "Commission",
      navGroup: "my-stats",
      module: "financial",
      permFlag: "view_commission",
      title: "Your commission",
      body: "My Commission shows what you have earned, what has been paid, and what is still outstanding -- updated as your deals register.",
      placement: "right",
    },
    {
      id: "targets",
      route: "/crm/kpis",
      target: "nav-/crm/targets",
      section: "Targets",
      navGroup: "my-stats",
      permFlag: "view_commission",
      title: "Your targets",
      body: "My Targets tracks your personal sales targets and how close you are to each one, so you always know what is left to do.",
      placement: "right",
    },
    {
      id: "monthly-activity",
      route: "/crm/kpis",
      target: "nav-/crm/kpis",
      section: "Monthly Activity",
      navGroup: "my-stats",
      title: "Your monthly activity",
      body: "Monthly Activity is your activity barometer -- viewings, valuations and the rhythm of your month -- so you can see the effort behind the results.",
      placement: "right",
    },
    {
      id: "leaderboard",
      route: "/crm/kpis",
      target: "nav-/crm/leaderboard",
      section: "Leaderboard",
      navGroup: "my-stats",
      module: "financial",
      permFlag: "view_commission",
      title: "The leaderboard",
      body: "See where you stand against the rest of the team.",
      placement: "right",
    },
    {
      // My Compliance (#2452): the agent's OWN compliance documents. Ungated --
      // every agent keeps their own documents current, whether or not they see
      // commission -- so it never filters out of the My Stats tour.
      id: "my-compliance",
      route: "/crm/kpis",
      target: "nav-/crm/my-documents",
      section: "My Compliance",
      navGroup: "settings",
      title: "Your own compliance",
      body: "My Compliance is where your own documents and their status live -- your FFC, ID and qualifications. Keep them current so you stay compliant.",
      placement: "right",
    },
    {
      // Confirm KPIs (#2452): the agent confirms their own monthly KPI figures.
      // Ungated -- part of every agent's month-end routine.
      id: "confirm-kpis",
      route: "/crm/kpis",
      target: "nav-/crm/kpi-confirm",
      section: "Confirm KPIs",
      navGroup: "my-stats",
      title: "Confirm your KPIs",
      body: "Each month you confirm your own KPI figures here, so your numbers are agreed before the month is locked. That is My Stats -- explore at your own pace.",
      placement: "right",
    },
  ],
};

/**
 * Workspace -- Tasks + Agenda.
 */
export const WORKSPACE_TOUR: TourDefinition = {
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  id: "agent-workspace",
  title: "Workspace tour",
  steps: [
    {
      id: "welcome",
      route: "/crm/tasks",
      target: "center",
      section: "Welcome",
      title: "Your day",
      body: "My Workspace keeps your day in order -- your follow-up tasks and your agenda of viewings and meetings. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "tasks",
      route: "/crm/tasks",
      target: "page-tasks",
      section: "Tasks",
      title: "Your tasks",
      body: "Every follow-up you owe, in one list. Nothing waiting on you slips through here.",
      placement: "bottom",
    },
    {
      id: "agenda",
      route: "/crm/tasks",
      target: "nav-/crm/appointments",
      section: "Agenda",
      navGroup: "workspace",
      title: "Your agenda",
      body: "Agenda lays out your viewings, valuations, meetings and calls so you can plan your week at a glance. That is your Workspace.",
      placement: "right",
    },
  ],
};

/**
 * Comms & Docs -- Messaging, Document Vault.
 * Messaging gates on the ``messaging`` module, the Vault on ``documents``.
 */
export const COMMS_DOCS_TOUR: TourDefinition = {
  audience: { excludePreset: BACK_OFFICE_PRESETS },
  id: "agent-comms-docs",
  title: "Comms and Docs tour",
  steps: [
    {
      id: "welcome",
      route: "/crm/document-vault",
      target: "center",
      section: "Welcome",
      title: "Talking and sharing",
      body: "Comms and Docs is how you reach your contacts and find the company paperwork you need. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "vault",
      route: "/crm/document-vault",
      target: "page-document-vault",
      section: "Document Vault",
      module: "documents",
      title: "The company library",
      body: "The Document Vault is the shared company library -- OTP templates, mandates, disclosures and the like, ready for any agent to use. A property, buyer or seller's own documents live on that record, not here.",
      placement: "bottom",
    },
    {
      id: "messaging",
      route: "/crm/document-vault",
      target: "nav-/crm/messaging",
      section: "Messaging",
      navGroup: "comms-docs",
      module: "messaging",
      title: "Messaging",
      body: "Send and track WhatsApp and email to your contacts from one place, with the full thread kept against each person. That is Comms and Docs.",
      placement: "right",
    },
  ],
};

/** Every agent section mini-tour, in nav order. */
export const AGENT_SECTION_TOURS: TourDefinition[] = [
  PROSPECTING_TOUR,
  LISTINGS_TOUR,
  DEALS_TOUR,
  MY_STATS_TOUR,
  WORKSPACE_TOUR,
  COMMS_DOCS_TOUR,
];
